// Public "Vienna vs X" comparison pages (/compare and /compare/<slug>).
//
// These exist so search engines and AI answer engines have a first-party,
// balanced description of how Vienna differs from the tools people already
// ask about. Rules for editing:
// - Describe competitors only by what they publicly say they are. No pricing,
//   ratings, or performance claims about another vendor.
// - Every Vienna claim must match src/content/product-documentation.md. The
//   site-wide bans in publicProductClaims.test.jsx apply here too ("24/7",
//   "never sleeps", "no missed deadlines", "never log in").
// - Keep "when to choose them" honest. A one-sided page is less likely to be
//   cited, and it is wrong.
// - Bump COMPARISONS_LAST_REVIEWED whenever any entry changes.

export const COMPARISONS_LAST_REVIEWED = "October 9, 2026";

const VIENNA_SUMMARY =
  "Vienna is a legal operations AI agent that works inside Nougat, a legal workspace. You email Vienna at vienna@nougat.law or message her in Slack or Microsoft Teams, and she does the work across your firm's email, calendar, documents, practice management, and court dockets, then reports back in the same thread.";

const VIENNA_ROWS = {
  model: "An agent you delegate matter work to by email, Slack, or Teams. She works across your connected tools and replies in the same thread.",
  research: "Finds and summarizes authorities with links to the source opinions, and checks case-law quotes against the original opinion text before showing them.",
  citations: "Checks every cite in a draft, by email or in the public Cite Checker, to confirm each authority exists and its quotes match.",
  drafting: "Drafts complaints, motions, client correspondence, and engagement letters inside the matter, in your firm's voice.",
  dockets: "Connects to PACER for federal dockets, pulls deadlines from filings and correspondence, and keeps them on the matter calendar.",
  integrations: "Gmail, Outlook and Microsoft 365, Slack, Teams, Zoom, Google Drive, OneDrive, Dropbox, iManage, Clio, PACER, and firm single sign-on.",
  audience: "Litigators, small and mid-size firms, legal teams, and businesses, with workspaces for civil litigation, criminal defense, startups and VC, and in-house teams.",
};

const ROW_LABELS = {
  model: "How you work with it",
  research: "Legal research",
  citations: "Citation checking",
  drafting: "Drafting",
  dockets: "Dockets and deadlines",
  integrations: "Where it works",
  audience: "Built for",
};

const SHARED_FAQS = [
  {
    question: "Is Vienna a law firm?",
    answer:
      "No. Vienna is legal software made by Nougat Legal Technology Corporation Corporation. Nothing Vienna produces is legal advice, and a qualified person should review her work before it is filed, sent, or relied on.",
  },
  {
    question: "What is the difference between Nougat and Vienna?",
    answer:
      "Nougat is the legal workspace that holds your matters, documents, dockets, and connections. Vienna is the AI agent who works in it. You give Vienna work by email, Slack, or Teams.",
  },
];

export const COMPARISONS = [
  {
    slug: "vienna-vs-cocounsel",
    competitor: "CoCounsel",
    competitorMaker: "Thomson Reuters",
    title: "Vienna vs CoCounsel | Nougat",
    description:
      "How Vienna, a legal operations agent that works from email, Slack, and Teams, compares with Thomson Reuters CoCounsel for research, drafting, citations, and matter work.",
    competitorSummary:
      "CoCounsel is Thomson Reuters' generative AI assistant for legal professionals. Thomson Reuters describes it as working with Westlaw and Practical Law content for research, document review, and drafting.",
    competitorRows: {
      model: "An AI assistant you open and prompt, connected to Thomson Reuters products.",
      research: "Research grounded in Westlaw content, for teams that already subscribe to Westlaw.",
      citations: "See Thomson Reuters for current citation features.",
      drafting: "Drafting and document review tasks inside the CoCounsel interface.",
      dockets: "Not positioned as a docket-monitoring or matter-operations tool.",
      integrations: "Thomson Reuters products, including Westlaw and Practical Law.",
      audience: "Legal professionals and firms using Thomson Reuters research products.",
    },
    chooseThem:
      "You live in Westlaw and want an AI assistant grounded in Thomson Reuters' research content.",
    chooseVienna:
      "You want an agent that runs matter work from your inbox: reading filings and correspondence, tracking federal dockets and deadlines, drafting in the matter, and checking cites, across the tools your firm already uses.",
    faqs: [
      {
        question: "Can I use Vienna and CoCounsel together?",
        answer:
          "Yes. They do different jobs. CoCounsel is a research and drafting assistant tied to Thomson Reuters content; Vienna handles matter operations across your email, calendar, documents, practice management, and dockets.",
      },
    ],
  },
  {
    slug: "vienna-vs-harvey",
    competitor: "Harvey",
    competitorMaker: "Harvey",
    title: "Vienna vs Harvey | Nougat",
    description:
      "How Vienna, a legal operations agent for litigators and smaller firms, compares with Harvey for research, drafting, citations, and day-to-day matter work.",
    competitorSummary:
      "Harvey is a generative AI platform for law firms and corporate legal teams. Harvey describes it as supporting research, drafting, document analysis, and workflows, and it is widely adopted by large law firms.",
    competitorRows: {
      model: "A platform your team logs into for AI-assisted research, drafting, and document analysis.",
      research: "AI-assisted research and analysis across legal and firm documents, per Harvey.",
      citations: "Not described by Harvey as a standalone cite checker.",
      drafting: "Drafting and document analysis across transactional and litigation work.",
      dockets: "Not positioned as a docket-monitoring tool.",
      integrations: "See Harvey for current integrations.",
      audience: "Large law firms and corporate legal departments.",
    },
    chooseThem:
      "You are a large firm or legal department looking for an enterprise AI platform across practice groups.",
    chooseVienna:
      "You are a litigator, small or mid-size firm, or legal team that wants an agent to take work off your plate by email, Slack, or Teams, with dockets, deadlines, cite checking, and drafting tied to each matter.",
    faqs: [
      {
        question: "Is Vienna only for small firms?",
        answer:
          "No. Vienna is built for litigators, law firms, legal teams, and businesses, with workspaces for civil litigation, criminal defense, startups and VC, and in-house teams.",
      },
    ],
  },
  {
    slug: "vienna-vs-lexis-plus-ai",
    competitor: "Lexis+ AI",
    competitorMaker: "LexisNexis",
    title: "Vienna vs Lexis+ AI | Nougat",
    description:
      "How Vienna, a legal operations agent that works from email, Slack, and Teams, compares with LexisNexis Lexis+ AI for research, citations, drafting, and matter work.",
    competitorSummary:
      "Lexis+ AI is LexisNexis' generative AI legal research and drafting assistant. LexisNexis describes it as grounded in Lexis content, with conversational search, summarization, and drafting.",
    competitorRows: {
      model: "A research assistant inside the Lexis+ platform.",
      research: "Conversational research grounded in LexisNexis' legal content.",
      citations: "Citation validation tied to Shepard's, per LexisNexis.",
      drafting: "Drafting assistance within Lexis+.",
      dockets: "Not positioned as a matter-operations agent.",
      integrations: "LexisNexis products.",
      audience: "Legal professionals who research in Lexis+.",
    },
    chooseThem:
      "Your research workflow is built on Lexis+ and you want generative AI inside that research platform.",
    chooseVienna:
      "You want the work around research handled too: filings and correspondence read from your inbox, federal dockets and deadlines tracked, drafts produced in the matter, and every cite checked before you file.",
    faqs: [
      {
        question: "Does Vienna replace a legal research subscription?",
        answer:
          "Not necessarily. Vienna finds and summarizes authorities with links to the source opinions, but firms that rely on a commercial research platform can keep it. Vienna's focus is running the matter work around the research.",
      },
    ],
  },
];

export const getComparison = (slug) => COMPARISONS.find((entry) => entry.slug === slug) || null;

export const buildComparisonRows = (comparison) =>
  Object.keys(ROW_LABELS).map((key) => ({
    key,
    label: ROW_LABELS[key],
    vienna: VIENNA_ROWS[key],
    competitor: comparison.competitorRows[key],
  }));

export const getComparisonFaqs = (comparison) => [...comparison.faqs, ...SHARED_FAQS];

export const buildComparisonJsonLd = (comparison) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: getComparisonFaqs(comparison).map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});

export { VIENNA_SUMMARY };
